import * as React from 'react';

// Pills & chips — mirror Docs/Design Handoff/portal/common.jsx. Inline-style +
// token idiom (matches AlertBanner/StatusBadge). Uppercase, rounded, semantic.

export interface PillProps {
  bg: string;
  fg: string;
  children: React.ReactNode;
  dot?: boolean;
  border?: string;
  style?: React.CSSProperties;
}

export function Pill({ bg, fg, children, dot, border, style }: PillProps) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 99,
        background: bg, color: fg, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
        letterSpacing: '.04em', border: border || 'none', whiteSpace: 'nowrap', ...style,
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: fg }} />}
      {children}
    </span>
  );
}

// Churn risk pill
const CHURN: Record<string, { bg: string; fg: string }> = {
  critical: { bg: '#FEE2E2', fg: '#DC2626' },
  high: { bg: '#FFEDD5', fg: '#C2410C' },
  medium: { bg: '#FEF9C3', fg: '#A16207' },
  low: { bg: '#DCFCE7', fg: '#15803D' },
};
export function ChurnPill({ level }: { level: string }) {
  const c = CHURN[level] || CHURN.low;
  return <Pill bg={c.bg} fg={c.fg}>{level}</Pill>;
}

// Severity pill (vulnerabilities + audit)
const SEV: Record<string, { bg: string; fg: string }> = {
  critical: { bg: '#FEE2E2', fg: '#DC2626' },
  high: { bg: '#FFEDD5', fg: '#C2410C' },
  medium: { bg: '#FEF9C3', fg: '#A16207' },
  low: { bg: '#E0E7FF', fg: '#4338CA' },
  info: { bg: '#F1F5F9', fg: '#475569' },
};
export function SevPill({ level }: { level: string }) {
  const s = SEV[level] || SEV.info;
  return <Pill bg={s.bg} fg={s.fg}>{level}</Pill>;
}

// Tier pill
const TIER: Record<string, { bg: string; fg: string }> = {
  trial: { bg: '#F3F4F6', fg: '#6B7280' },
  startup: { bg: '#DBEAFE', fg: '#1D4ED8' },
  professional: { bg: '#E0E7FF', fg: '#4338CA' },
  enterprise: { bg: '#FEF3C7', fg: '#92400E' },
};
const TIER_LABEL: Record<string, string> = { trial: 'TRIAL', startup: 'START', professional: 'PRO', enterprise: 'ENT' };
export function TierPill({ tier }: { tier: string }) {
  const t = TIER[tier] || TIER.trial;
  const lbl = TIER_LABEL[tier] || tier;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 4, background: t.bg, color: t.fg, fontSize: 11, fontWeight: 600, letterSpacing: '.04em' }}>
      {lbl}
    </span>
  );
}

// Billing-state chip (included / metered / overage)
const BILLING: Record<string, { bg: string; fg: string }> = {
  included: { bg: '#DCFCE7', fg: '#15803D' },
  metered: { bg: '#E0E7FF', fg: '#4338CA' },
  overage: { bg: '#FEE2E2', fg: '#DC2626' },
};
export function BillingChip({ state }: { state: string }) {
  const b = BILLING[state] || BILLING.included;
  return <Pill bg={b.bg} fg={b.fg}>{state}</Pill>;
}
